import { type FormEvent, useState } from "react"
import { toast } from "sonner"
import { type CurrentUser, api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card"
import { Lock } from "lucide-react"

type Props = { currentUser: CurrentUser }

export function AccountPage({ currentUser }: Props) {
  const [current, setCurrent] = useState("")
  const [next, setNext] = useState("")
  const [confirm, setConfirm] = useState("")
  const [loading, setLoading] = useState(false)

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (next !== confirm) {
      toast.error("A nova senha e a confirmação não coincidem.")
      return
    }
    setLoading(true)
    try {
      const res = await api.changePassword(current, next)
      toast.success(res.log || "Senha alterada com sucesso.")
      setCurrent("")
      setNext("")
      setConfirm("")
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Erro ao alterar senha.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="flex min-h-full min-w-0 flex-1 flex-col gap-6 p-4 sm:p-6 md:p-8">
      <div>
        <h1 className="text-lg font-semibold tracking-tight">Minha conta</h1>
        <p className="text-sm text-muted-foreground">
          Gerencie seu perfil e segurança.
        </p>
      </div>

      <div className="grid min-w-0 grid-cols-1 gap-4 xl:grid-cols-2 xl:items-start">
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <div className="flex size-8 items-center justify-center rounded-lg border bg-muted/40">
                <Lock className="size-4 text-muted-foreground" />
              </div>
              <div>
                <CardTitle className="text-sm font-medium">
                  Alterar senha
                </CardTitle>
                <CardDescription className="text-xs">
                  Mín. 8 caracteres: maiúscula, minúscula, número e símbolo (! @
                  # $ % * - _)
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="flex flex-col gap-4">
              <div className="space-y-2">
                <Label htmlFor="current">Senha atual</Label>
                <Input
                  id="current"
                  type="password"
                  autoComplete="current-password"
                  value={current}
                  onChange={(e) => setCurrent(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="new-pw">Nova senha</Label>
                <Input
                  id="new-pw"
                  type="password"
                  autoComplete="new-password"
                  value={next}
                  onChange={(e) => setNext(e.target.value)}
                  disabled={loading}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirm-pw">Confirmar nova senha</Label>
                <Input
                  id="confirm-pw"
                  type="password"
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  disabled={loading}
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !current || !next || !confirm}
              >
                {loading ? "Salvando..." : "Salvar nova senha"}
              </Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-sm font-medium">Informações</CardTitle>
            <CardDescription className="text-xs">
              Dados da sua conta
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-col gap-3">
            <Row label="Usuário" value={currentUser.username} mono />
            <Row label="Nome" value={currentUser.fullName || "-"} />
            <Row label="E-mail" value={currentUser.email || "-"} />
            <Row
              label="Permissões"
              value={
                currentUser.permissions.length
                  ? currentUser.permissions.join(", ")
                  : "nenhuma"
              }
              mono
            />
          </CardContent>
        </Card>
      </div>
    </div>
  )
}

function Row({
  label,
  value,
  mono,
}: {
  label: string
  value: string
  mono?: boolean
}) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-b pb-2 last:border-0 last:pb-0">
      <span className="shrink-0 text-xs text-muted-foreground">{label}</span>
      <span
        className={`truncate text-right text-sm ${mono ? "font-mono" : ""}`}
      >
        {value}
      </span>
    </div>
  )
}
